import { signal } from '@angular/core';
import { debounceTime, distinctUntilChanged, Subject, takeUntil } from 'rxjs';

export const TypeaheadPageSize = 10;
export const TypeaheadDebounceMs = 250;
export const TypeaheadScrollThreshold = 100;
export const NameUniquenessDebounceMs = 300;

export type AlgoliaTypeaheadPage<T> = {
	hits: T[];
	page: number;
	nbPages: number;
	nbHits: number;
};

/**
 * Builds an Algolia `filters` clause that excludes the given object IDs.
 */
export function excludeObjectIdFilter(ids: Iterable<string>): string {
	return Array.from(ids)
		.filter(Boolean)
		.map((id) => `NOT objectID:${id}`)
		.join(' AND ');
}

export function localTypeaheadPage<T>(
	items: T[],
	query: string,
	page: number,
	stringify: (item: T) => string = (item) => String(item),
	size = TypeaheadPageSize,
): AlgoliaTypeaheadPage<T> {
	const needle = query.trim().toLowerCase();
	const filtered = needle
		? items.filter((item) => stringify(item).toLowerCase().includes(needle))
		: items;
	const start = page * size;

	return {
		hits: filtered.slice(start, start + size),
		page,
		nbPages: Math.ceil(filtered.length / size) || 0,
		nbHits: filtered.length,
	};
}

export function facetValueKeys(
	facets: Record<string, Record<string, number>> | undefined,
	facet: string,
): string[] {
	return Object.keys(facets?.[facet] ?? {}).sort((left, right) =>
		left.localeCompare(right),
	);
}

/**
 * Dedupes in-flight and completed promises by cache key so the same Algolia
 * (or other) request is not issued twice while a result is still valid.
 */
export class AlgoliaRequestCache<T> {
	private readonly inflight = new Map<string, Promise<T>>();

	get(
		key: string,
		factory: () => Promise<T>,
		options: { fresh?: boolean } = {},
	): Promise<T> {
		if (options.fresh) this.inflight.delete(key);

		const existing = this.inflight.get(key);
		if (existing) return existing;

		const pending = factory().catch((error) => {
			this.inflight.delete(key);
			throw error;
		});
		this.inflight.set(key, pending);
		return pending;
	}

	clear() {
		this.inflight.clear();
	}
}

/**
 * Paginated typeahead over an Algolia (or Algolia-shaped) search. Debounces
 * query changes, caches pages per query, coalesces in-flight requests, and
 * exposes an infinite-scroll loader.
 */
export class AlgoliaTypeahead<T> {
	readonly items = signal<T[]>([]);
	readonly loading = signal(false);
	readonly loadingMore = signal(false);
	readonly hasMore = signal(false);
	readonly query = signal('');

	private readonly query$ = new Subject<string>();
	private readonly destroy$ = new Subject<void>();
	private readonly pageCache = new Map<string, AlgoliaTypeaheadPage<T>>();
	private readonly inflight = new Map<
		string,
		Promise<AlgoliaTypeaheadPage<T>>
	>();

	private latestQuery = '';
	private currentQuery = '';
	private currentPage = 0;

	constructor(
		private readonly fetchPage: (
			query: string,
			page: number,
		) => Promise<AlgoliaTypeaheadPage<T>>,
		private readonly debounceMs = TypeaheadDebounceMs,
	) {
		this.query$
			.pipe(
				debounceTime(this.debounceMs),
				distinctUntilChanged(),
				takeUntil(this.destroy$),
			)
			.subscribe((query) => {
				void this.load(query, 0, false);
			});
	}

	setQuery(raw: string) {
		this.query$.next(raw.trim());
	}

	/**
	 * Loads the first page immediately (no debounce). No-ops when a first page
	 * is already in memory or a load is in flight.
	 */
	ensureLoaded() {
		if (this.items().length) return Promise.resolve();
		return this.load(this.currentQuery, 0, false);
	}

	onScroll(event: Event) {
		const target = event.currentTarget as HTMLElement | null;
		if (!target) return Promise.resolve();

		const remaining =
			target.scrollHeight - (target.clientHeight + target.scrollTop);
		if (remaining < TypeaheadScrollThreshold) {
			return this.loadMore();
		}

		return Promise.resolve();
	}

	loadMore() {
		if (!this.hasMore() || this.loading() || this.loadingMore()) {
			return Promise.resolve();
		}

		return this.load(this.currentQuery, this.currentPage + 1, true);
	}

	clearCache() {
		this.pageCache.clear();
		this.inflight.clear();
		this.items.set([]);
		this.hasMore.set(false);
		this.currentPage = 0;
	}

	destroy() {
		this.destroy$.next();
		this.destroy$.complete();
	}

	private cacheKey(query: string, page: number) {
		return `${query}::${page}`;
	}

	private getOrFetch(query: string, page: number) {
		const key = this.cacheKey(query, page);
		const cached = this.pageCache.get(key);
		if (cached) return Promise.resolve(cached);

		const existing = this.inflight.get(key);
		if (existing) return existing;

		const pending = this.fetchPage(query, page)
			.then((response) => {
				this.pageCache.set(key, response);
				this.inflight.delete(key);
				return response;
			})
			.catch((error) => {
				this.inflight.delete(key);
				throw error;
			});

		this.inflight.set(key, pending);
		return pending;
	}

	private async load(query: string, page: number, append: boolean) {
		if (!append) this.latestQuery = query;

		if (append) this.loadingMore.set(true);
		else this.loading.set(true);

		try {
			const response = await this.getOrFetch(query, page);
			if (query !== this.latestQuery) return;

			this.currentQuery = query;
			this.query.set(query);
			this.currentPage = response.page;
			this.hasMore.set(response.page + 1 < response.nbPages);

			if (append) {
				this.items.update((current) => [...current, ...response.hits]);
			} else {
				this.items.set(response.hits);
			}
		} catch (error) {
			if (query === this.latestQuery) {
				console.error('AlgoliaTypeahead.load', error);
			}
		} finally {
			if (query === this.latestQuery) {
				this.loading.set(false);
				this.loadingMore.set(false);
			}
		}
	}
}
